import { flip, offset, type Placement, shift } from '@floating-ui/dom';
import { useDismiss, useFloating, useHover, useInteractions } from '@floating-ui/react';
import React, { type ReactNode } from 'react';
import { Transition } from 'react-transition-group';

import Portal from '#@/plumbing/Portal';
import { CARD_OPENING_TRANSITION_DURATION } from '#@/shared/constants';
import type { DocumentElement } from '#@/shared/types';
import { isEnter } from '#@/shared/utils/transitionStates';

import useTooltipState from './hooks/useTooltipState';
import { MINI_TOOLTIP_EDGE_OFFSET, MINI_TOOLTIP_OFFSET, MiniTooltipCard } from './Styles';

const ON_MOUSE_ENTER_DEFAULT_DELAY = 0;

export interface MiniTooltipProps<TriggerElement> {
  children: (props: {
    ref: React.Ref<TriggerElement>;
    getReferenceProps: (userProps?: React.HTMLProps<Element>) => Record<string, unknown>;
  }) => ReactNode;
  /** Text shown in the tooltip. */
  bodyText?: React.ReactNode | string | null;
  /**
   * Placement of the tooltip. Default: 'bottom'.
   * For valid values, see https://floating-ui.com/docs/computePosition#placement
   */
  placement?: Placement;
  /** Time in ms to wait before opening the tooltip on hover. */
  onMouseEnterDelay?: number;
  /** Offset of the tooltip from the trigger element. */
  offsetValue?: number;
  /** Allows clients to control whether or not the tooltip is open. */
  isOpen?: boolean;
  /** Allows the component to set the open state maintained by the client. */
  setIsOpen?: (value: boolean) => void;
  /**
   * If set to `true`, the tooltip is opened/closed depending on whether the user
   * hovers the cursor over the trigger element. The default is `true`.
   */
  hoverEnabled?: boolean;
  /**
   * If set to `true`, the tooltip is closed when the user clicks somewhere
   * outside it or presses the escape key. The default is `true`.
   */
  dismissEnabled?: boolean;

  isTriggerElementClickable?: boolean;
}

const MiniTooltip = <TriggerElement extends DocumentElement>({
  children,
  bodyText,
  placement,
  onMouseEnterDelay = ON_MOUSE_ENTER_DEFAULT_DELAY,
  offsetValue = MINI_TOOLTIP_OFFSET,
  isOpen: isOpenOverride,
  setIsOpen: setIsOpenOverride,
  hoverEnabled = true,
  dismissEnabled = true,
  isTriggerElementClickable,
}: MiniTooltipProps<TriggerElement>): ReturnType<React.FC<MiniTooltipProps<TriggerElement>>> => {
  const { isOpen, openTooltip, closeTooltip } = useTooltipState({
    isOpenOverride,
    setIsOpenOverride,
  });

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => (open ? openTooltip() : closeTooltip()),
    placement,
    strategy: 'fixed',
    middleware: [
      shift({ padding: MINI_TOOLTIP_EDGE_OFFSET }),
      offset(offsetValue),
      flip({ padding: MINI_TOOLTIP_EDGE_OFFSET }),
    ],
  });

  const referencePress = isTriggerElementClickable;
  const move = !referencePress;

  const hover = useHover(context, {
    enabled: hoverEnabled,
    move,
    delay: { open: onMouseEnterDelay },
  });
  const dismiss = useDismiss(context, {
    enabled: dismissEnabled,
    referencePress,
    bubbles: { escapeKey: true },
    ancestorScroll: true,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss]);

  return (
    <>
      {children({
        ref: refs.setReference,
        getReferenceProps,
      })}
      {bodyText && (
        <Transition
          in={isOpen}
          timeout={CARD_OPENING_TRANSITION_DURATION}
          unmountOnExit
          nodeRef={refs.floating}
        >
          {(state) => (
            <Portal>
              <MiniTooltipCard
                {...getFloatingProps()}
                ref={refs.setFloating}
                $isOpen={isEnter(state)}
                data-cy-tooltip
                data-cy-tooltip-open={isEnter(state)}
                style={floatingStyles}
              >
                {bodyText}
              </MiniTooltipCard>
            </Portal>
          )}
        </Transition>
      )}
    </>
  );
};

export default MiniTooltip;
